import * as deepmerge from 'deepmerge';
import { HintType, ReticleType } from '../../idscan/enums/Camera';
import { JourneyEvent } from '../../idscan/enums/JourneyEvent';
import { TranslationKey } from '../../idscan/enums/Translation';
import { ICameraOptions } from '../../idscan/interfaces/CameraConfiguration';
import { IOverlayConfiguration } from '../../idscan/interfaces/ContainerConfiguration';
import { retryOperation, wait } from '../functions/functions';
import {
	ICamera,
	IDeviceCapabilities,
	IResolution,
} from '../interfaces/Camera';
import EventService from './EventService';
import { ExceptionManager } from './ExceptionManager';
import { isFirefox } from 'mobile-device-detect';

export enum CameraPermissionState {
	UNKNOWN = 'unknown',
	PROMPT = 'prompt',
	GRANTED = 'granted',
	DENIED = 'denied',
}

export enum CameraFacingMode {
	FRONT = 'FRONT',
	BACK = 'BACK',
	ENVIRONMENT = 'ENVIRONMENT', //new facing mode for devices have multiple cameras
}

/**
 * @internal
 */
export interface IStreamRenderOptions {
	grayscale?: boolean;
	mirror?: boolean;
	reticleType?: ReticleType;
	hintType?: HintType;
	watermark?: boolean;
}

const defaultDocumentReticlePath = (
	width: number,
	height: number,
	inset: number = 50,
	thickness: number = 5,
	length: number = width > height ? 600 : 700
) => `
	M${inset},${inset} L${inset + thickness + length},${inset} L${inset + thickness + length
	},${inset + thickness} L${inset + thickness},${inset + thickness} L${inset + thickness
	},${inset + thickness + length} L${inset},${inset + thickness + length
	} L${inset},${inset} Z
	M${width - inset - length - thickness},${inset} L${width - inset},${inset} L${width - inset
	},${inset + thickness + length} L${width - inset - thickness},${inset + thickness + length
	} L${width - inset - thickness},${inset + thickness} L${width - inset - length - thickness
	},${inset + thickness} L${width - inset - length - thickness},${inset} Z
	M${width - inset - thickness},${height - inset - thickness - length} L${width - inset
	},${height - inset - thickness - length} L${width - inset},${height - inset} L${width - inset - thickness - length
	},${height - inset} L${width - inset - thickness - length},${height - inset - thickness
	} L${width - inset - thickness},${height - inset - thickness} L${width - inset - thickness
	},${height - inset - thickness - length} Z
	M${inset},${height - inset - thickness - length} L${inset + thickness},${height - inset - thickness - length
	} L${inset + thickness},${height - inset - thickness} L${inset + thickness + length
	},${height - inset - thickness} L${inset + thickness + length},${height - inset
	} L${inset},${height - inset} L${inset},${height - inset - thickness - length} Z
`;

const defaultPortraitReticlePath = (width: number, height: number) => {
	const widthRatio = (width > height ? 576 : 360) / width;
	const heightRatio = (width > height ? 360 : 576) / height;
	const ovalHeight = 295 / heightRatio;

	let reticle = `M 0 0 m ${width / 2} ${(height - ovalHeight) / 2 + ovalHeight
		} c ${50 / widthRatio} ${0 / heightRatio} ${110 / widthRatio} -${70 / heightRatio
		} ${110 / widthRatio} -${155 / heightRatio} c ${0 / widthRatio} -${90 / heightRatio
		} -${50 / widthRatio} -${140 / heightRatio} -${110 / widthRatio} -${140 / heightRatio
		} c -${65 / widthRatio} ${0 / heightRatio} -${110 / widthRatio} ${50 / heightRatio
		} -${110 / widthRatio} ${140 / heightRatio} c ${0 / widthRatio} ${85 / heightRatio
		} ${50 / widthRatio} ${155 / heightRatio} ${110 / widthRatio} ${155 / heightRatio
		}`;

	return reticle;
};

const defaultWatermarkPath = (
	width: number,
	height: number,
	logoWidth: number = 160,
	logoHeight: number = 31
) => `
	M${width / 2 - logoWidth / 2
	},${logoHeight} m22.03-12.583l-10.9664 0l-.9078 2.9528l8.6788 0c-.2796 1.7674-1.1798 3.3848-2.5464 4.575c-1.3666 1.19-3.1146 1.8788-4.9442 1.9482c-1.8296.0692-3.627-.4852-5.0838-1.5682c-1.457-1.0832-2.4828-2.6276-2.9016-4.3686c-.4188-1.741-.2048-3.57.6058-5.1736c.8104-1.6036 2.1668-2.882 3.8364-3.6158c1.6698-.734 3.5488-.8778 5.3152-.4068c1.7664.471 3.31 1.5274 4.3662 2.9882h3.6948c-1.1234-2.4514-3.1362-4.4098-5.6504-5.4976c-2.5142-1.0878-5.3516-1.2282-7.9654-.3938c-2.6138.8342-4.8188 2.5842-6.19 4.9122c-1.3712 2.328-1.8116 5.0694-1.2362 7.6958c.5754 2.6264 2.1258 4.9516 4.3524 6.5276c2.2266 1.576 4.9718 2.2912 7.7062 2.0076c2.7344-.2836 5.2646-1.5458 7.1026-3.5434c1.8382-1.9976 2.854-4.589 2.852-7.2748c-.0022-.5938-.0508-1.1868-.1452-1.7734zm11.1572-8.9288c1.1604-.0356 2.3026.2884 3.2632.9256c.9604.637 1.6898 1.5546 2.084 2.6212c.9078 2.1192.599 4.9566-1.607 6.4906c1.037.3658 1.928 1.0438 2.5448 1.9362c.6168.8924.9274 1.9532.8868 3.0294c0 3.2186-2.1696 6.4728-6.3548 6.4728h-9.9678l0-21.4758l9.1508 0zm-6.0006 18.4258h6.7904c.4462-.0072.886-.1034 1.2924-.283c.4066-.1794.7712-.4382 1.0714-.7606c.3002-.3222.5298-.7012.6744-1.1134c.1446-.4122.2012-.849.1664-1.2834c.0256-.4128-.0408-.826-.1944-1.2114c-.1536-.3854-.3906-.7336-.6952-1.021c-.3046-.2874-.669-.5068-1.0688-.6434c-.3996-.1366-.8246-.187-1.2462-.1478h-6.7904l0 6.464zm0-9.541l5.9372 0c3.4588 0 3.5222-5.9674.0634-5.9674l-6.0006 0v5.9674zm36.4944.0442l-10.9664 0l-.9078 2.9528l8.7422 0c-.2794 1.7674-1.1798 3.3848-2.5464 4.575c-1.3666 1.19-3.1146 1.8788-4.9442 1.9482c-1.8296.0694-3.627-.4852-5.0838-1.5682c-1.457-1.0832-2.4828-2.6276-2.9018-4.3686c-.4188-1.741-.2048-3.57.6058-5.1736c.8104-1.6036 2.1668-2.882 3.8364-3.6158c1.6698-.734 3.5488-.8778 5.3152-.4068c1.7664.471 3.31 1.5274 4.3662 2.9882h3.6948c-1.1274-2.4444-3.1412-4.3948-5.6532-5.4752c-2.5122-1.0804-5.3448-1.2144-7.9522-.3762c-2.6072.8382-4.8046 2.5894-6.1686 4.916c-1.364 2.3266-1.798 5.064-1.2184 7.6846c.5794 2.6206 2.1316 4.939 4.357 6.5082c2.2256 1.5694 4.967 2.2784 7.6962 1.991c2.729-.2876 5.2524-1.5518 7.084-3.5486c1.8318-1.9968 2.8418-4.5852 2.8356-7.2662c-.0006-.5944-.0522-1.1876-.1542-1.7734zm8.425-13.442v30.4404h1.2v-30.4404h-1.2zm9.0852 5.205l3.2046 0v20.9528h-3.2046v-20.9528zm7.7074.1948c2.038-.3326 4.0982-.5194 6.164-.5586c7.072 0 10.3764 3.4226 10.3764 10.8532c0 7.4306-3.268 10.7912-10.6032 10.7912c-1.9854-.011-3.9682-.1414-5.9372-.3902v-20.6954zm5.8646 18.2748c5.2744 0 7.3714-2.1192 7.3714-7.9182c0-5.799-2.097-8.14-7.1718-8.14c-.9592.0134-1.9168.0784-2.8686.195v15.7388c.9712.0976 1.9698.1242 2.669.1242zm12.7186-.3546c1.4916.3236 3.0116.5078 4.539.5498c1.9064 0 3.3046-.4878 3.3046-1.8532c0-1.3656-1.271-1.5962-3.5314-2.3144c-2.542-.8158-4.7026-1.7734-4.7026-4.4334c0-2.66 2.2968-4.4334 6.1642-4.4334c1.46.0122 2.9146.1756 4.3394.4876l-.3996 2.5802c-1.3172-.2728-2.6574-.427-4.0034-.461c-2.0064 0-3.005.6206-3.005 1.667c0 1.3034 1.507 1.5252 3.7676 2.2788c2.7234.8866 4.4754 1.8532 4.4754 4.4956c0 2.9614-2.1696 4.655-6.5362 4.655c-1.6286-.011-3.251-.1954-4.8388-.5496l.4268-2.669zm12.3736-4.762c0-4.7526 1.8974-7.7852 7.2626-7.7852c1.37.0142 2.7342.1778 4.067.4876l-.3178 2.5094c-1.197-.229-2.4118-.3596-3.6312-.3902c-3.1048 0-4.2668 1.4986-4.2668 5.2492c0 3.1568.8352 5.2138 3.9308 5.2138c1.4338-.0596 2.8464-.3594 4.176-.8866l.8262 2.4472c-1.6252.6814-3.37 1.0488-5.1384 1.0818c-4.775 0-6.9084-2.8728-6.9084-7.927zm19.5178-1.463c.9476.0022 1.894.0674 2.8326.195v-1.0374c0-2.1546-1.0714-2.873-3.1684-2.873c-1.501.0484-2.9908.2686-4.4392.6562l-.5356-2.5094c1.6862-.4596 3.4244-.7128 5.1746-.7538c3.6312 0 6.0006 1.472 6.0006 5.1518v10.2326h-2.2332l-.3358-1.2768c-1.2772 1.0312-2.8822 1.5956-4.5392 1.596c-2.8688 0-5.2744-1.596-5.2744-4.6906c0-3.0946 2.442-4.6906 6.518-4.6906zm-.699 6.943c1.2762-.0134 2.5142-.4268 3.5316-1.1794v-3.4138c-.908-.0974-2.1334-.1684-2.8052-.1684c-2.0972 0-3.4316.7182-3.4316 2.4118c0 1.6936 1.0984 2.3498 2.7052 2.3498zm10.2038-12.937h2.2696l.236 1.3034c1.4438-1.0304 3.178-1.6004 4.9658-1.6316c3.2682 0 5.0022 1.9242 5.0022 5.0542v10.33h-3.0322v-9.9754c0-1.8886-.9078-2.802-2.6326-2.802c-1.3446.0542-2.6492.4616-3.7766 1.1794v11.598h-3.0322v-15.0562z
`;

const autoCaptureOnHint = (
	width: number,
	height: number,
	logoWidth: number = 261.61,
	logoHeight: number = 18
) => `
	M${width / 2 - logoWidth / 2},${height - logoHeight
	} m 3.4828 0 l 1.4865 -4.4417 l 6.7726 0 l 1.4952 4.4417 h 3.483 l -6.3503 -18.0131 l -4.0283 0 l -6.3414 18.0131 h 3.483 z m 2.366 -7.0628 l 2.4363 -7.2562 h 0.1408 l 2.4363 7.2562 l -5.0135 0 z m 25.0941 -10.9503 l 0 11.4957 c 0 2.2605 -1.5744 3.8789 -4.0459 3.8789 c -2.4803 0 -4.0459 -1.6184 -4.0459 -3.8789 l 0 -11.4957 h -3.2632 l 0 11.7683 c 0 3.87 2.9024 6.5175 7.309 6.5175 c 4.3889 0 7.309 -2.6475 7.309 -6.5175 v -11.7683 h -3.2632 z m 6.5136 2.7354 l 5.5676 0 l 0 15.2777 h 3.2368 l 0 -15.2777 l 5.5674 0 v -2.7354 l -14.3718 0 v 2.7354 z m 32.9594 6.2713 c 0 -5.8225 -3.5534 -9.2529 -8.2764 -9.2529 c -4.7319 0 -8.2764 3.4302 -8.2764 9.2529 c 0 5.8139 3.5445 9.2529 8.2764 9.2529 c 4.7231 0 8.2764 -3.4302 8.2764 -9.2529 z m -3.2805 0 c 0 4.0987 -2.0582 6.3239 -4.9959 6.3239 c -2.9288 0 -4.9959 -2.2252 -4.9959 -6.3239 c 0 -4.0987 2.0671 -6.3239 4.9959 -6.3239 c 2.9377 0 4.9959 2.2252 4.9959 6.3239 z m 28.9183 -2.9288 c -0.554 -4.0019 -3.6324 -6.3239 -7.6257 -6.3239 c -4.7055 0 -8.25 3.4302 -8.25 9.2529 c 0 5.8139 3.5005 9.2529 8.25 9.2529 c 4.2834 0 7.1243 -2.7705 7.6257 -6.2095 l -3.2894 -0.0175 c -0.4134 2.1109 -2.1373 3.2983 -4.2921 3.2983 c -2.9201 0 -5.0135 -2.1901 -5.0135 -6.3239 c 0 -4.0636 2.0758 -6.3239 5.0221 -6.3239 c 2.1901 0 3.9053 1.2403 4.2834 3.3951 h 3.2894 z m 5.5792 11.9355 l 1.4865 -4.4417 h 6.7724 l 1.4954 4.4417 h 3.483 l -6.3503 -18.0131 l -4.0283 0 l -6.3416 18.0131 h 3.483 z m 2.366 -7.0628 l 2.4363 -7.2562 h 0.1408 l 2.4363 7.2562 h -5.0135 z m 13.7392 7.0628 h 3.263 v -6.0865 h 3.4479 c 4.1602 0 6.3943 -2.4979 6.3943 -5.9633 c 0 -3.439 -2.2077 -5.9633 -6.3503 -5.9633 l -6.7549 0 v 18.0131 z m 3.263 -8.7691 v -6.5175 h 2.9905 c 2.4452 0 3.527 1.3193 3.527 3.2368 c 0 1.9175 -1.0817 3.2807 -3.5094 3.2807 h -3.0081 z m 12.3803 -6.5086 h 5.5674 v 15.2777 h 3.2368 v -15.2777 h 5.5676 v -2.7354 l -14.3718 0 v 2.7354 z m 28.9926 -2.7354 v 11.4957 c 0 2.2605 -1.5744 3.8789 -4.0459 3.8789 c -2.4803 0 -4.0459 -1.6184 -4.0459 -3.8789 v -11.4957 h -3.2632 v 11.7683 c 0 3.87 2.9026 6.5175 7.309 6.5175 c 4.3889 0 7.309 -2.6475 7.309 -6.5175 v -11.7683 h -3.2632 z m 7.3053 18.0131 l 3.263 0 v -6.5965 h 3.3247 l 3.5358 6.5965 h 3.6413 l -3.8964 -7.1419 c 2.1197 -0.8531 3.2368 -2.665 3.2368 -5.1103 c 0 -3.439 -2.2163 -5.761 -6.3503 -5.761 l -6.7549 0 v 18.0131 z m 3.263 -9.3055 v -5.9809 h 2.9905 c 2.4363 0 3.527 1.117 3.527 3.0345 c 0 1.9175 -1.0906 2.9466 -3.5094 2.9466 h -3.0081 z m 13.3413 9.3055 l 11.7859 0 v -2.7354 l -8.5229 0 v -4.9167 l 7.8457 0 v -2.7354 h -7.8457 v -4.8902 h 8.4525 v -2.7354 l -11.7155 0 v 18.0131 z m 30.2262 -10.3171 l -8.1095 0 v 2.6211 l 8.1095 0 v -2.6211 z m 26.7205 1.3104 c 0 -5.8225 -3.5542 -9.2529 -8.2766 -9.2529 c -4.7328 0 -8.2766 3.4302 -8.2766 9.2529 c 0 5.8139 3.5439 9.2529 8.2766 9.2529 c 4.7224 0 8.2766 -3.4302 8.2766 -9.2529 z m -3.2818 0 c 0 4.0987 -2.0578 6.3239 -4.9949 6.3239 c -2.9288 0 -4.9969 -2.2252 -4.9969 -6.3239 c 0 -4.0987 2.0681 -6.3239 4.9969 -6.3239 c 2.9371 0 4.9949 2.2252 4.9949 6.3239 z m 21.6637 -9.0065 l -3.2446 0 v 12.2785 h -0.1589 l -8.4789 -12.2785 l -2.9206 0 v 18.0131 h 3.2632 v -12.2697 h 0.1507 l 8.4872 12.2697 l 2.902 0 v -18.0131 z
`;

const autoCaptureOffHint = (
	width: number,
	height: number,
	logoWidth: number = 273.16,
	logoHeight: number = 18
) => `
	M${width / 2 - logoWidth / 2},${height - logoHeight
	} m 3.4805 0 l 1.4854 -4.4385 l 6.7676 0 l 1.4941 4.4385 h 3.4805 l -6.3457 -17.9999 l -4.0254 0 l -6.3369 17.9999 h 3.4805 z m 2.3643 -7.0576 l 2.4346 -7.2509 h 0.1406 l 2.4346 7.251 l -5.0098 -0 z m 25.0757 -10.9423 l 0 11.4873 c 0 2.2588 -1.5733 3.876 -4.0429 3.876 c -2.4785 0 -4.0429 -1.6172 -4.0429 -3.876 l 0 -11.4873 h -3.2608 l 0 11.7597 c 0 3.8672 2.9005 6.5127 7.3037 6.5127 c 4.3857 0 7.3037 -2.6455 7.3037 -6.5127 v -11.7597 h -3.2608 z m 6.509 2.7334 l 5.5634 0 l 0 15.2666 h 3.2344 l 0 -15.2666 l 5.5634 0 v -2.7334 l -14.3611 0 v 2.7334 z m 32.9353 6.2666 c 0 -5.8183 -3.5508 -9.2461 -8.2706 -9.2461 c -4.7285 0 -8.2704 3.4277 -8.2704 9.2461 c 0 5.8096 3.5419 9.2461 8.2704 9.2461 c 4.7198 0 8.2706 -3.4277 8.2706 -9.2461 z m -3.2783 0 c 0 4.0957 -2.0567 6.3193 -4.9923 6.3193 c -2.9267 0 -4.9921 -2.2236 -4.9921 -6.3193 c 0 -4.0957 2.0654 -6.3193 4.9921 -6.3193 c 2.9355 0 4.9923 2.2236 4.9923 6.3193 z m 28.8974 -2.9267 c -0.5538 -3.999 -3.63 -6.3193 -7.6201 -6.3193 c -4.7023 0 -8.2442 3.4277 -8.2442 9.2461 c 0 5.8096 3.498 9.2461 8.2442 9.2461 c 4.2801 0 7.1191 -2.7685 7.6201 -6.2051 l -3.2872 -0.0176 c -0.4131 2.1094 -2.1357 3.2959 -4.289 3.2959 c -2.918 0 -5.0098 -2.1885 -5.0098 -6.3193 c 0 -4.0605 2.0742 -6.3193 5.0185 -6.3193 c 2.1885 0 3.9024 1.2393 4.2803 3.3926 l 3.2872 0 z m 5.5749 11.9267 l 1.4854 -4.4385 h 6.7675 l 1.4943 4.4385 h 3.4805 l -6.3457 -17.9999 h -4.0254 l -6.337 17.9999 l 3.4805 0 z m 2.3642 -7.0576 l 2.4346 -7.251 h 0.1407 l 2.4346 7.251 h -5.0098 z m 13.7292 7.0576 h 3.2606 v -6.082 l 3.4454 0 c 4.1572 0 6.3896 -2.4961 6.3896 -5.959 c 0 -3.4365 -2.206 -5.959 -6.3457 -5.959 l -6.7499 0 v 17.9999 z m 3.2606 -8.7627 v -6.5127 h 2.9883 c 2.4434 0 3.5244 1.3183 3.5244 3.2344 c 0 1.916 -1.081 3.2783 -3.5069 3.2783 h -3.0059 z m 12.3712 -6.5039 h 5.5636 v 15.2666 h 3.2342 v -15.2666 h 5.5636 v -2.7334 l -14.3613 0 v 2.7334 z m 28.9714 -2.7334 v 11.4873 c 0 2.2588 -1.5731 3.876 -4.0429 3.876 c -2.4785 0 -4.0429 -1.6172 -4.0429 -3.876 v -11.4873 h -3.2608 v 11.7597 c 0 3.8672 2.9005 6.5127 7.3037 6.5127 c 4.3857 0 7.3037 -2.6455 7.3037 -6.5127 v -11.7597 h -3.2608 z m 7.3 17.9999 l 3.2608 0 v -6.5918 h 3.3223 l 3.5331 6.5918 h 3.6387 l -3.8936 -7.1367 c 2.1182 -0.8525 3.2344 -2.6631 3.2344 -5.1064 c 0 -3.4365 -2.2147 -5.7568 -6.3457 -5.7568 l -6.7499 0 v 17.9999 z m 3.2608 -9.2988 v -5.9765 l 2.9881 0 c 2.4346 0 3.5244 1.1162 3.5244 3.0322 c 0 1.916 -1.0898 2.9443 -3.5069 2.9443 l -3.0057 0 z m 13.3313 9.2988 l 11.7773 0 v -2.7334 l -8.5167 0 v -4.9131 l 7.84 0 v -2.7334 h -7.84 v -4.8867 h 8.4463 v -2.7334 l -11.7069 0 v 17.9999 z m 30.2042 -10.3095 l -8.1035 0 v 2.6191 l 8.1035 0 v -2.6191 z m 26.701 1.3096 c 0 -5.8183 -3.5516 -9.2461 -8.2706 -9.2461 c -4.7293 0 -8.2706 3.4277 -8.2706 9.2461 c 0 5.8096 3.5413 9.2461 8.2706 9.2461 c 4.719 0 8.2706 -3.4277 8.2706 -9.2461 z m -3.2794 0 c 0 4.0957 -2.0563 6.3193 -4.9912 6.3193 c -2.9267 0 -4.9933 -2.2236 -4.9933 -6.3193 c 0 -4.0957 2.0666 -6.3193 4.9933 -6.3193 c 2.9349 0 4.9912 2.2236 4.9912 6.3193 z m 6.8557 9 h 3.2608 v -7.6465 h 7.4807 v -2.7334 l -7.4807 0 v -4.8867 l 8.2706 0 v -2.7334 l -11.5314 0 v 17.9999 z m 14.9985 0 h 3.2608 v -7.6465 h 7.4786 v -2.7334 l -7.4786 0 v -4.8867 l 8.2706 0 v -2.7334 l -11.5314 0 v 17.9999 z
`;

const defaultConfiguration: {
	overlay: IOverlayConfiguration;
	cameraOptions: ICameraOptions;
} = {
	overlay: {
		documentReticle: {
			path: defaultDocumentReticlePath,
		},
		portraitReticle: {
			path: defaultPortraitReticlePath,
		},
	},
	cameraOptions: {
		resolutions: {
			ideal: {
				horizontal: 1440,
				vertical: 900,
			},
			minimum: {
				horizontal: 1280,
				vertical: 720,
			},
		},
	},
};

/**
 * @internal
 */
export class CameraService {
	private _stream?: MediaStream;
	private _configuration?: {
		overlay: IOverlayConfiguration;
		cameraOptions: ICameraOptions;
	};

	private _currentDeviceId?: string;
	private _lastActiveDeviceId?: string;

	private _animationHandle?: number;
	private _videoContext?: HTMLVideoElement;

	private _reticlePath?: Path2D;
	private _reticleType?: boolean;

	private _availableCameras: MediaDeviceInfo[] = [] as MediaDeviceInfo[];

	private _lastCameraPermissionState?: CameraPermissionState;

	private _observedDeviceCapabilities: IDeviceCapabilities = {
		cameras: {},
	};

	/**
	 * Get the capabilities of the device as has been observed by the camera
	 * service.
	 */
	public get observedDeviceCapabilities(): IDeviceCapabilities {
		return deepmerge({}, this._observedDeviceCapabilities);
	}

	/**
	 * Set the observed capabilities of the device as observed by the camera
	 * service
	 */
	public set observedDeviceCapabilities(value: IDeviceCapabilities) {
		this._observedDeviceCapabilities = deepmerge(
			this._observedDeviceCapabilities,
			value
		);

		EventService.publish(JourneyEvent.CAMERA_CAPABILITIES_UPDATED, {
			capabilities: this._observedDeviceCapabilities,
		});
	}

	/**
	 * Get the current media stream
	 */
	public get stream() {
		return this._stream;
	}

	/**
	 * Get the ID of the device associated with the current media stream
	 */
	public get currentDeviceId(): string | undefined {
		return this._currentDeviceId;
	}

	/**
	 * Probe each camera present on the device for information about its
	 * capabilities, then cache the findings for later use.
	 */
	public doDeviceProbe = () => {
		return this.getAllDeviceCapabilities().then(
			(devices) =>
			(this.observedDeviceCapabilities = {
				cameras: devices.reduce((out, curr) => ({ ...out, ...curr }), {}),
			})
		);
	};

	/**
	 * Check if the device has a camera which is capable of capturing video at
	 * the specified resolution.
	 */
	public hasDeviceCapableOfResolution(
		{ horizontal, vertical }: IResolution = this._configuration!.cameraOptions
			.resolutions.minimum
	): boolean {
		if (isFirefox) {
			//firefox support workaround;
			//firefox can not get resolution from device capabilities
			return true;
		}
		return Object.keys(this.observedDeviceCapabilities.cameras).some(
			(deviceId) => {
				const maximumResolution =
					this.observedDeviceCapabilities.cameras[deviceId].resolutions.maximum;
				return (
					maximumResolution.horizontal >= horizontal &&
					maximumResolution.vertical >= vertical
				);
			}
		);
	}

	/**
	 * Checks if the camera label indicates a back camera.
	 */
	public isBackCameraLabel(cameraLabel: string): boolean {
		const backCameraKeywords = [
			'rear',
			'back',
			'rück',
			'arrière',
			'trasera',
			'trás',
			'traseira',
			'posteriore',
			'后面',
			'後面',
			'背面',
			'задней',
			'الخلفية',
			'후',
			'arka',
			'achterzijde',
			'หลัง',
			'baksidan',
			'bagside',
			'sau',
			'bak',
			'tylny',
			'takakamera',
			'belakang',
			'אחורית',
			'πίσω',
			'spate',
			'hátsó',
			'zadní',
			'darrere',
			'zadná',
			'задня',
			'stražnja',
			'belakang',
			'बैक',
		];
		// Check the script of the cameraLabel to determine if .toLowerCase() is needed
		const isLatinScript = /[a-z]/i.test(cameraLabel); // Checks if the label contains Latin characters

		// If it's Latin-based, apply .toLowerCase(), otherwise, skip it
		return backCameraKeywords.some((keyword) =>
			isLatinScript
				? cameraLabel.toLowerCase().includes(keyword.toLowerCase())
				: cameraLabel.includes(keyword)
		);
	}

	/**
	 * Checks if the camera label indicates a back dual wide camera.
	 */
	public isBackDualWideCameraLabel(cameraLabel: string): boolean {
		const backDualWideCameraKeywords = [
			'Fotocamera doppia con grandangolo (posteriore)',
			'Cámara trasera dual con gran angular',
			'Rückseitige Dual-Weitwinkelkamera',
			'Double appareil photo grand angle arrière',
			'Back Dual Wide Camera',
			'Tylny dwuobiektywowy aparat szerokokątny',
			'Cameră dublă cu obiectiv superangular spate',
			'Zadná duálna širokouhlá kamera',
			'Zadní duální širokoúhlý fotoaparát',
			'Dubbel vidvinkelkamera på baksidan',
			'Çift Geniş Kamera Arka Yüzü',
			'Dubbele groothoekcamera aan de achterkant',
			'كاميرا خلفية مزدوجة عريضة'
		];

		// Check the script of the cameraLabel to determine if .toLowerCase() is needed
		const isLatinScript = /[a-z]/i.test(cameraLabel); // Checks if the label contains Latin characters

		// If it's Latin-based, apply .toLowerCase(), otherwise, skip it
		return backDualWideCameraKeywords.some((keyword) =>
			isLatinScript
				? cameraLabel.toLowerCase().includes(keyword.toLowerCase())
				: cameraLabel.includes(keyword)
		);
	}

	/**
	 * Creates a promise of stream and returns it.
	 */
	public getStream = (
		deviceId: string | undefined = this._lastActiveDeviceId,
		facingMode: CameraFacingMode | undefined = undefined,
		minimumResolution: IResolution = this._configuration!.cameraOptions
			.resolutions.minimum,
		idealResolution: IResolution = this._configuration!.cameraOptions
			.resolutions.ideal
	): Promise<MediaStream> => {
		return this.stopStreaming()
			.then(() => {
				return this.getVideoCameras().then((cameras) => {
					if (facingMode !== undefined) {
						let facingCameras: MediaDeviceInfo[];
						if (facingMode === CameraFacingMode.BACK) {
							facingCameras = cameras.filter((camera) =>
								this.isBackCameraLabel(camera.label)
							);
						} else if (facingMode === CameraFacingMode.ENVIRONMENT) {
							facingCameras = cameras.filter((camera) => this.isBackDualWideCameraLabel(camera.label));
						} else {
							facingCameras = cameras.filter(
								(camera) => !this.isBackCameraLabel(camera.label)
							);
						}

						facingCameras.sort((camera1, camera2) =>
							camera1.label.localeCompare(camera2.label)
						);
						deviceId =
							facingCameras.length > 0 ? facingCameras[0].deviceId : deviceId;
					}
					return this.getUserMedia({
						video: {
							width: {
								min: minimumResolution.horizontal,
								ideal: idealResolution.horizontal,
							},
							height: {
								min: minimumResolution.vertical,
								ideal: idealResolution.vertical,
							},
							...(deviceId ? { deviceId: { exact: deviceId } } : {}),
						},
					});
				});
			})
			.then((stream) => (this._stream = stream))
			.then(this.reconcileDevice)
			.then((device) => {
				this._lastActiveDeviceId = device ? device.deviceId : undefined;
				this._currentDeviceId = device ? device.deviceId : undefined;

				return device;
			})
			.then((device) => this.getDeviceCapabilities(this._stream!, device))
			.then((capabilities) => {
				this.observedDeviceCapabilities = {
					cameras: { ...capabilities },
				};
			})
			.then(() =>
				EventService.publish(JourneyEvent.CAMERA_READY, {
					camera: this._currentDeviceId,
				})
			)
			.then(() => this._stream!);
	};

	/**
	 * Stops stream to broadcast if there is a stream
	 */
	public stopStreaming = (): Promise<void> =>
		new Promise((resolve) => {
			cancelAnimationFrame(this._animationHandle!);

			if (this._stream) {
				this._stream
					.getVideoTracks()
					.forEach((track: MediaStreamTrack) => track.stop());
				this._stream = undefined;

				return EventService.publish(JourneyEvent.CAMERA_STOP, {
					camera: this._lastActiveDeviceId,
				}).then(() => resolve());
			} else {
				return resolve();
			}
		});

	/**
	 * Get all available video cameras in this device
	 */
	public getVideoCameras = () => {
		if (navigator.mediaDevices && this._availableCameras.length === 0) {
			return navigator.mediaDevices
				.enumerateDevices()
				.then((devices) => devices.filter(({ kind }) => kind === 'videoinput'))
				.then((devices) => {
					if (devices.some((dev) => dev.label !== '')) {
						this._availableCameras = devices;
					}
					if (isFirefox) this.getVideoCameras();
					return devices;
				});
		} else {
			return Promise.resolve(this._availableCameras);
		}
	};

	/**
	 * Check what the current permissions state is for the
	 */
	public getCameraPermission = (): Promise<CameraPermissionState> =>
		new Promise((resolve) => {
			if (isFirefox) {
				return resolve(this.decideCameraPermission());
			} else if ((navigator as any).permissions) {
				var rr = (navigator as any).permissions.query({ name: 'camera' });
				rr.then(({ state }: { state: CameraPermissionState }) => {
					if (state === CameraPermissionState.PROMPT) {
						return resolve(this.decideCameraPermission());
					} else return resolve(state);
				}).catch(() => {
					resolve(CameraPermissionState.UNKNOWN);
				});
			} else {
				return resolve(this.decideCameraPermission());
			}
		});

	/**
	 * Decide camera permission based on the camera accessibility if permission is unknown
	 */
	private decideCameraPermission = (): Promise<CameraPermissionState> =>
		new Promise((resolve) => {
			if (
				this._lastCameraPermissionState !== null &&
				this._lastCameraPermissionState !== undefined
			) {
				return resolve(this._lastCameraPermissionState);
			} else {
				var cameraPermissionState = CameraPermissionState.UNKNOWN;
				return this.getVideoCameras()
					.then((cameras) => {
						//triggering camera to use highest resolution
						//even if it does not support 1920x1080, it will use
						//the highest resolution close to following constraint
						const constraints = {
							video: {
								width: 1920,
								height: 1080,
								facingMode: { ideal: 'environment' },
							},
							audio: false,
						};
						return this.getUserMedia(constraints)
							.then((value) => {
								cameraPermissionState = CameraPermissionState.GRANTED;

								//stop all cameras after we get the camera permission state by accessing it in behind
								value.getVideoTracks().forEach((track) => track.stop());

								return cameraPermissionState;
							})
							.catch((exception) => {
								console.log(exception);
								cameraPermissionState = CameraPermissionState.DENIED;
								return cameraPermissionState;
							});
					})
					.then((permissionState) => {
						this._lastCameraPermissionState = permissionState;
						return resolve(permissionState);
					});
			}
		});

	/**
	 * Check if this service has an active stream
	 */
	public hasActiveStream = () =>
		this._stream ? this._stream.getTracks().length > 0 : false;

	/**
	 * Get an image from the current video stream
	 */
	public getImageFromCurrentStream = (
		options: IStreamRenderOptions = {}
	): Promise<string> =>
		this.resizeCanvas(this._videoContext!, document.createElement('canvas'))
			.then((canvas) => canvas.getContext('2d')!)
			.then((context) =>
				this.renderFrameToContext(context, options).canvas.toDataURL(
					'image/jpeg'
				)
			);

	/**
	 * Get a blob from the current video stream
	 */
	public getBlobFromCurrentStream = (
		options: IStreamRenderOptions = {}
	): Promise<Blob> =>
		new Promise((resolve) => {
			return this.resizeCanvas(
				this._videoContext!,
				document.createElement('canvas')
			)
				.then((canvas) => canvas.getContext('2d')!)
				.then((context) =>
					this.renderFrameToContext(context, options).canvas.toBlob(
						(blob) => resolve(blob as Blob),
						'image/jpeg'
					)
				);
		});

	public getLivenessBlobFromCurrentStream = (
		options: IStreamRenderOptions = {}
	): Promise<Blob> =>
		new Promise((resolve) => {
			return this.resizeLivenessCanvas(
				this._videoContext!,
				document.createElement('canvas')
			)
				.then((canvas) => {
					return canvas.getContext('2d')!;
				})
				.then((context) =>
					this.renderLivenessFrameToContext(context, options).canvas.toBlob(
						(blob) => resolve(blob as Blob),
						'image/jpeg'
					)
				);
		});

	/**
	 * Render the contents of a stream to the specified element until stopped
	 */
	public renderStreamToElement = (
		stream: MediaStream | undefined = this._stream,
		canvas?: HTMLCanvasElement,
		options: IStreamRenderOptions = {}
	): Promise<void> => {
		if (!stream || !canvas) {
			return Promise.resolve();
		} else {
			const { reticleType = ReticleType.NONE, hintType = HintType.NONE } =
				options;

			this._videoContext = document.createElement('video');

			// NOTE: These need to be set or else iOS refuses to work properly.
			this._videoContext.setAttribute('autoplay', 'true');
			this._videoContext.setAttribute('loop', 'true');
			this._videoContext.setAttribute('muted', 'true');
			this._videoContext.setAttribute('playsinline', 'true');

			this._videoContext.srcObject = stream;

			return this._videoContext!.play()
				.then(() => {
					const resizeCanvasPromise = this.resizeCanvas(
						this._videoContext!,
						canvas
					);
					EventService.publish(JourneyEvent.CAMERA_CANVAS_RESIZE, {
						isPortrait:
							this._videoContext!.videoHeight > this._videoContext!.videoWidth,
					});
					return resizeCanvasPromise;
				})
				.then((c) => this.setReticlePath(reticleType, c.width, c.height))
				.then(() => {
					this._videoContext!.addEventListener('resize', () =>
						setTimeout(() => {
							this.resizeCanvas(this._videoContext!, canvas).then((c) =>
								this.setReticlePath(reticleType, c.width, c.height)
							);
						}, 250)
					);
				})
				.then(() => {
					cancelAnimationFrame(this._animationHandle!);
					this._animationHandle = this.doFrameCycle(
						canvas.getContext('2d')!,
						options
					);
				});
		}
	};

	/**
	 * Set the configuration
	 */
	public setConfiguration = ({
		overlay = {} as any,
		cameraOptions = {} as any,
	}: Partial<{
		overlay: IOverlayConfiguration;
		cameraOptions: ICameraOptions;
	}>) => {
		this._configuration = deepmerge(defaultConfiguration, {
			overlay,
			cameraOptions,
		});
	};

	/**
	 * Clear the canvas completely
	 */
	public clearCanvas = (canvas?: HTMLCanvasElement) => {
		if (canvas instanceof HTMLCanvasElement) {
			const context = canvas.getContext('2d')!;

			context.globalCompositeOperation = 'source-over';
			context.fillStyle = 'black';
			context.fillRect(0, 0, canvas.width, canvas.height);
		}
	};

	/**
	 * Complete a single render cycle for the camera viewfinder using the
	 * specified options
	 */
	private doFrameCycle = (
		context: CanvasRenderingContext2D,
		options: IStreamRenderOptions
	) =>
		requestAnimationFrame(() => {
			const {
				reticleType,
				watermark = true,
				hintType = HintType.NONE,
			} = options;
			this.renderFrameToContext(context, options);

			if (reticleType) {
				this.renderReticles(context);
			}
			this.renderHintType(context, hintType);
			if (watermark) {
				this.renderWatermark(context);
			}

			this._animationHandle = this.doFrameCycle(context, options);
		});

	/**
	 * Render the reticles onto the specified camera viewport
	 */
	private renderReticles = (context: CanvasRenderingContext2D): void => {
		if (this._reticlePath) {
			if (!this._reticleType) {
				context.fillStyle = 'rgba(255, 255, 255, 0.75)';
				context.globalCompositeOperation = 'difference';
				context.fill(this._reticlePath);
			} else {
				context.fillStyle = 'rgba(0, 0, 0, 0)';
				context.fill(new Path2D(this._reticlePath));
				context.strokeStyle = 'white';
				context.lineWidth = 10;
				context.stroke(this._reticlePath);
			}

			context.globalCompositeOperation = 'source-over';
		}
	};
	/**
	 * Render the hintType onto the specified camera viewport
	 */
	private renderHintType = (
		context: CanvasRenderingContext2D,
		hintType: HintType
	): void => {
		context.fillStyle = 'rgba(255, 255, 255, 0.75)';
		switch (hintType) {
			case HintType.AUTOCAPTUREOFF:
				context.fill(
					new Path2D(
						autoCaptureOffHint(context.canvas.width, context.canvas.height)
					)
				);
				break;
			case HintType.AUTOCAPTUREON:
				context.fill(
					new Path2D(
						autoCaptureOnHint(context.canvas.width, context.canvas.height)
					)
				);
				break;
			default:
				break;
		}
	};

	/**
	 * Render the watermark onto the specified camera viewport
	 */
	private renderWatermark = (context: CanvasRenderingContext2D): void => {
		context.fillStyle = 'rgba(255, 255, 255, 0.75)';
		context.fill(
			new Path2D(
				defaultWatermarkPath(context.canvas.width, context.canvas.height)
			)
		);
		context.globalCompositeOperation = 'source-over';
	};

	/**
	 * Resize the specified canvas to match the size of the specified video
	 * element
	 */
	private resizeCanvas = (
		video: HTMLVideoElement,
		canvas: HTMLCanvasElement
	): Promise<HTMLCanvasElement> =>
		new Promise((resolve) => {
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;

			resolve(canvas);
		});

	private resizeLivenessCanvas = (
		video: HTMLVideoElement,
		canvas: HTMLCanvasElement
	): Promise<HTMLCanvasElement> =>
		new Promise((resolve) => {
			canvas.width = video.videoWidth / 3;
			canvas.height = video.videoHeight / 3;

			resolve(canvas);
		});

	/**
	 * Process a single frame and render it to the specified element
	 */
	private renderFrameToContext = (
		context: CanvasRenderingContext2D,
		options: IStreamRenderOptions = {}
	): CanvasRenderingContext2D => {
		if (this._videoContext) {
			// context.fillStyle = 'rgba(255, 255, 255, 1)';
			// context.fillRect(0, 0, context.canvas.width, context.canvas.height);

			if (options.grayscale) {
				context.globalCompositeOperation = 'luminosity';
			}
			context.globalCompositeOperation = 'source-over';
			if (options.mirror) {
				context.setTransform(-1, 0, 0, 1, context.canvas.width, 0);
				context.drawImage(
					this._videoContext,
					0,
					0,
					this._videoContext.videoWidth,
					this._videoContext.videoHeight,
					0,
					0,
					this._videoContext.videoWidth,
					this._videoContext.videoHeight
				);
				context.setTransform(1, 0, 0, 1, 0, 0);
			} else {
				context.drawImage(
					this._videoContext,
					0,
					0,
					this._videoContext.videoWidth,
					this._videoContext.videoHeight,
					0,
					0,
					this._videoContext.videoWidth,
					this._videoContext.videoHeight
				);
			}
			context.globalCompositeOperation = 'source-over';
		}

		return context;
	};

	private renderLivenessFrameToContext = (
		context: CanvasRenderingContext2D,
		options: IStreamRenderOptions = {}
	): CanvasRenderingContext2D => {
		if (this._videoContext) {
			context.fillStyle = 'rgba(255, 255, 255, 1)';
			context.fillRect(0, 0, context.canvas.width, context.canvas.height);

			context.drawImage(
				this._videoContext,
				0,
				0,
				this._videoContext.videoWidth,
				this._videoContext.videoHeight,
				0,
				0,
				this._videoContext.videoWidth / 3,
				this._videoContext.videoHeight / 3
			);

			context.globalCompositeOperation = 'source-over';
		}

		return context;
	};

	/**
	 * Get the capabilities of all known cameras on this device
	 */

	private getAllDeviceCapabilities = (): Promise<
		Array<Record<string, ICamera>>
	> => {
		return this.getVideoCameras()
			.then((devices) => {
				return this.getCameraCapabilitiesSequentially(devices);
			})
			.catch((err) => {
				console.log(err);
				return [];
			});
	};

	//ENT-451
	private async getCameraCapabilitiesSequentially(devices: MediaDeviceInfo[]) {
		let camerasFuncs: (() => Promise<Record<string, ICamera>>)[];
		let result: Record<string, ICamera>[] = [];
		camerasFuncs = devices.map(
			(device) => () =>
				new Promise<Record<string, ICamera>>((resolve) =>
					resolve(this.getCameraCapabilities(device))
				)
		);
		for (let cameraFunc of camerasFuncs) {
			try {
				var res = await cameraFunc();
				result.push(res);
			} catch (error) {
				//console.log('Error occured while getting camera capability (this is mostly because that camera is being used by another application): ' + error);
			}
		}
		return result;
	}

	private getCameraCapabilities(
		device: MediaDeviceInfo
	): Promise<Record<string, ICamera>> {
		return this.getUserMedia({
			video: {
				...(device.deviceId ? { deviceId: { exact: device.deviceId } } : {}),
			},
		}).then((stream) => {
			stream.getVideoTracks().forEach((track) => track.stop());
			const capabilities = this.getDeviceCapabilities(stream, device);
			return capabilities;
		});
	}

	/**
	 * Get the user media given the specified constraints
	 */
	private getUserMedia = (
		constraints: MediaStreamConstraints
	): Promise<MediaStream> =>
		new Promise((resolve, reject) => {
			return retryOperation(
				() =>
					wait(200).then(
						async () => await navigator.mediaDevices.getUserMedia(constraints)
					),
				1000,
				3
			)
				.then(resolve)
				.catch((e: DOMException) => {
					switch (e.name) {
						case 'NotAllowedError':
						case 'PermissionDismissedError':
							return reject(
								ExceptionManager.process(
									new ExceptionManager.PlatformException(
										TranslationKey.EXCEPTION_PLATFORM_CAMERA_PERMISSION_DENIED,
										e
									)
								)
							);
						case 'NotReadableError':
							return reject(
								ExceptionManager.process(
									new ExceptionManager.PlatformException(
										TranslationKey.EXCEPTION_PLATFORM_CAMERA_NOT_READABLE,
										e
									)
								)
							);
						case 'OverconstrainedError':
							return reject(
								ExceptionManager.process(
									new ExceptionManager.PlatformException(
										TranslationKey.EXCEPTION_PLATFORM_CAMERA_NONE_SUITABLE,
										e
									)
								)
							);
						default: {
							console.log(e);
							return reject(
								ExceptionManager.process(
									new ExceptionManager.Exception(
										TranslationKey.EXCEPTION,
										undefined,
										e
									)
								)
							);
						}
					}
				});
		});

	/**
	 * Take the specified stream and use the device list to work out which
	 * device we're working with. This has to use the device labels because on
	 * older versions of iOS, the device IDs don't match up
	 */
	private reconcileDevice = (
		stream: MediaStream
	): Promise<MediaDeviceInfo | undefined> => {
		return this.getVideoCameras().then((cameras) => {
			return Array.from(
				new Set(
					stream.getVideoTracks().map((track) => {
						return cameras.find(
							(camera) =>
								camera.deviceId === track.getSettings().deviceId ||
								camera.label === track.label
						);
					})
				)
			)[0];
		});
	};

	/**
	 * Set the reticle path to use during the render cycles
	 */
	private setReticlePath = (
		reticleType: ReticleType,
		width: number,
		height: number
	) => {
		const { documentReticle, portraitReticle } = this._configuration!.overlay;

		switch (reticleType) {
			case ReticleType.DOCUMENT:
				this._reticlePath = new Path2D(documentReticle.path(width, height));
				this._reticleType = false;
				break;
			case ReticleType.PORTRAIT:
				this._reticlePath = new Path2D(portraitReticle.path(width, height));
				this._reticleType = true;
				break;
			case ReticleType.NONE:
				this._reticlePath = undefined;
			default:
				return;
		}
	};

	private GetCurrentTrackSettings(
		track: MediaStreamTrack
	): MediaTrackCapabilities {
		return {
			aspectRatio: {
				min: track.getSettings().aspectRatio,
				max: track.getSettings().aspectRatio,
			},
			frameRate: {
				min: track.getSettings().frameRate,
				max: track.getSettings().frameRate,
			},
			width: { min: track.getSettings().width, max: track.getSettings().width },
			height: {
				min: track.getSettings().height,
				max: track.getSettings().height,
			},
		};
	}

	/**
	 * Get the capabilities of the specified device from the specified stream
	 */
	private getDeviceCapabilities = (
		stream: MediaStream,
		{ deviceId, label, groupId }: MediaDeviceInfo = {
			label: '',
			deviceId: '',
			groupId: '',
		} as any
	): Promise<Record<string, ICamera>> =>
		stream
			.getVideoTracks()
			.map((track): ICamera => {
				const {
					aspectRatio = { min: 0, max: 0 },
					frameRate = { min: 0, max: 0 },
					width = { min: 0, max: 0 },
					height = { min: 0, max: 0 },
				} = typeof track.getCapabilities != 'function'
						? this.GetCurrentTrackSettings(track)
						: track.getCapabilities();

				const { facingMode = '' } = track.getSettings();

				return {
					aspectRatio: {
						minimum: (aspectRatio as any).min,
						maximum: (aspectRatio as any).max,
					},
					deviceId,
					deviceName: label,
					facingMode: facingMode as any as VideoFacingModeEnum,
					frameRate: {
						minimum: (frameRate as any).min!,
						maximum: (frameRate as any).max!,
					},
					groupId,
					resolutions: {
						minimum: {
							horizontal: (width as any).min!,
							vertical: (height as any).min!,
						},
						maximum: {
							horizontal: (width as any).max!,
							vertical: (height as any).max!,
						},
					},
				};
			})
			.reduce(
				(out, curr) => deepmerge(out, { [curr.deviceId]: curr }),
				{} as any
			);
}

export default new CameraService();
